/**
 * Zwevend contactblok — zichtbaarheid, mobiele toggle en sluiten.
 *
 * Het blokje wordt server-side met `hidden` gerenderd (zie
 * includes/floating-contact.php); deze module beslist of en wanneer het in beeld
 * komt. Zonder die volgorde zou het bij elke paginalading kort opflitsen voordat
 * de scroll-drempel verwerkt is.
 *
 * Drie dingen:
 *  - het blokje verschijnt zodra er ongeveer een halve schermhoogte gescrold is,
 *    met een korte fade (de --visible-class);
 *  - de sluitknop haalt het weg voor déze paginaweergave; de dismiss wordt
 *    bewust NIET onthouden (geen storage), zodat het contactmoment op elke
 *    pagina en na elke reload opnieuw kans krijgt;
 *  - op mobiel klapt een tik op de ronde avatar het paneel open en weer dicht
 *    (aria-expanded op de trigger). Vanaf md staat het paneel er permanent en is
 *    de trigger via CSS verborgen.
 *
 * Self-init op DOM-ready, no-op zonder doelen. Import in resources/js/main.js.
 */

/**
 * Initialiseert één zwevend contactblok.
 *
 * @param {HTMLElement} root - Het [data-floating-contact]-element.
 */
function initInstance(root) {
    const trigger = root.querySelector('[data-floating-contact-trigger]');
    const close = root.querySelector('[data-floating-contact-close]');

    // Drempel: een halve schermhoogte. Elke check leest 'm opnieuw, zodat een
    // resize of een in-/uitschuivende adresbalk meegenomen wordt.
    let revealed = false;

    const reveal = () => {
        if (revealed) {
            return;
        }
        revealed = true;
        window.removeEventListener('scroll', onScroll);

        root.hidden = false;

        // Eén frame wachten voordat de class erop gaat: het element moet eerst
        // zichtbaar zijn, anders springt 'ie er zonder fade in. Bewust NA het
        // wegnemen van hidden — in een achtergrond-tab is requestAnimationFrame
        // bevroren, en dan moet het blokje alsnog gewoon in de DOM staan; de fade
        // volgt zodra de tab weer zichtbaar is.
        window.requestAnimationFrame(() => {
            root.classList.add('floating-contact--visible');
        });
    };

    // Bewust geen rAF-throttle om de drempel te toetsen: dat is één vergelijking,
    // de listener is passive en verwijdert zichzelf zodra 'ie geraakt heeft.
    const onScroll = () => {
        if (window.scrollY > window.innerHeight * 0.5) {
            reveal();
        }
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    // Wie met een anker of een herstelde scrollpositie binnenkomt, staat al voorbij
    // de drempel — dan meteen tonen in plaats van wachten op een scroll-event.
    onScroll();

    if (trigger) {
        trigger.addEventListener('click', () => {
            const open = root.classList.toggle('floating-contact--open');
            trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
    }

    if (close) {
        close.addEventListener('click', () => {
            root.classList.remove('floating-contact--visible');
            root.remove();
            window.removeEventListener('scroll', onScroll);
        });
    }
}

/**
 * Initialiseert alle zwevende contactblokken binnen een scope.
 *
 * @param {ParentNode} [scope] - Zoekgebied; standaard het hele document.
 */
export function initFloatingContact(scope = document) {
    scope.querySelectorAll('[data-floating-contact]').forEach((root) => initInstance(root));
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initFloatingContact());
} else {
    initFloatingContact();
}
